import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PasswordInput,
  Switch,
  Table,
  type TableColumn,
} from "../../ds";
import type { DeviceDetail, WifiNetwork, WifiRadio, WifiStatus } from "../../lib/api";
import { isStation } from "../../lib/roles";
import { SectionCard } from "../SectionCard";
import { ProbeBanner } from "./ProbeBanner";
import { useConfirmProbe } from "../../lib/hooks/useConfirmProbe";
import { useSection } from "../../lib/hooks/useSection";

// Sektion „WLAN“ → actions `wifi_get` / `wifi_set`.
//
// Die Netze gehen als Desired-State raus (wie firewall_set): die Oberfläche
// schickt immer die vollständige Liste, ein entferntes Netz ist damit gelöscht.
// Der Schlüssel kommt nie vom Router zurück — leer lassen heißt „unverändert“.

/** Frist für WLAN-Änderungen an einem Uplink. Muss zu `WIFI_REVERT_IN` in
 *  `core/writes.py` passen — der Server schickt sie in der Antwort zurück, das
 *  hier ist nur der Startwert des Countdowns. Großzügiger als bei der Firewall:
 *  der STA muss erst wieder assoziieren, DHCP ziehen und der Tunnel neu
 *  handshaken, bevor überhaupt jemand bestätigen KANN. */
const WIFI_REVERT_IN = 180;

/** Was OpenWrt versteht, in der Reihenfolge, in der man es wählen will. */
const ENCRYPTIONS: [string, string][] = [
  ["psk2", "WPA2 (PSK)"],
  ["sae-mixed", "WPA2/WPA3 gemischt"],
  ["sae", "WPA3 (SAE)"],
  ["psk-mixed", "WPA/WPA2 gemischt"],
  ["none", "Offen (ohne Schlüssel)"],
];

/** Formularstand eines Netzes. `id` fehlt bei einem neuen. */
interface NetForm {
  id?: string;
  radio: string;
  mode: string;
  ssid: string;
  encryption: string;
  key: string;
  bssid: string;
  hidden: boolean;
  isolate: boolean;
  network: string;
  disabled: boolean;
}

function toNetForm(net: WifiNetwork, radio: string): NetForm {
  return {
    id: net.id,
    radio,
    mode: net.mode ?? "ap",
    ssid: net.ssid ?? "",
    encryption: net.encryption ?? "none",
    key: "",
    bssid: net.bssid ?? "",
    hidden: net.hidden ?? false,
    isolate: net.isolate ?? false,
    network: net.network ?? "lan",
    disabled: net.disabled ?? false,
  };
}

function emptyNetForm(radio: string): NetForm {
  return {
    radio,
    mode: "ap",
    ssid: "",
    encryption: "psk2",
    key: "",
    bssid: "",
    hidden: false,
    isolate: false,
    network: "lan",
    disabled: false,
  };
}

/** Ein Netz als Wire-Zeile. Ohne `id` legt der Agent es an, `radio` sagt wo. */
function netParams(form: NetForm): Record<string, unknown> {
  const row: Record<string, unknown> = {
    mode: form.mode,
    ssid: form.ssid,
    encryption: form.encryption,
    network: form.network,
    disabled: form.disabled,
  };
  if (form.id) row.id = form.id;
  else row.radio = form.radio;
  // Leer = nicht anfassen. Bei „offen“ ist der Schlüssel ohnehin gegenstandslos.
  if (form.key && form.encryption !== "none") row.key = form.key;
  // Nur ein AP versorgt Clients — „versteckt“ und „isoliert“ sind an einem
  // Client-Interface gegenstandslos, `bssid` umgekehrt nur dort sinnvoll.
  if (form.mode === "sta") {
    if (form.bssid.trim()) row.bssid = form.bssid.trim();
  } else {
    row.hidden = form.hidden;
    row.isolate = form.isolate;
  }
  return row;
}

/** Was am Formular gerade nicht stimmt — einmal berechnet für die Felder und
 *  für den Speichern-Knopf, damit beide dasselbe meinen. */
function netFormErrors(form: NetForm) {
  const keyTooShort = form.key.length > 0 && form.key.length < 8;
  // Ein Uplink darf nicht stillschweigend im LAN landen — das brückte das
  // lokale Netz mit einem fremden. Der Server weist das ab (writes.py::
  // wifi_set), die Oberfläche sagt es vorher.
  const staInLan = form.mode === "sta" && (!form.network.trim() || form.network.trim() === "lan");
  return { keyTooShort, staInLan, blocked: !form.ssid.trim() || keyTooShort || staInLan };
}

/** Die Felder eines Netzes. Ausgelagert, weil AP und Uplink verschiedene
 *  brauchen: „versteckt“ und „isoliert“ sind an einem Client-Interface
 *  gegenstandslos, `bssid` umgekehrt nur dort sinnvoll. */
function NetworkFields({ form, onChange }: { form: NetForm; onChange: (form: NetForm) => void }) {
  const sta = form.mode === "sta";
  const open = form.encryption === "none";
  const { keyTooShort, staInLan } = netFormErrors(form);

  return (
    <div className="flex flex-col gap-3">
      <Field label="Rolle" htmlFor="wifi-mode">
        <select
          id="wifi-mode"
          className="ds-input"
          value={form.mode}
          onChange={(e) =>
            onChange({
              ...form,
              mode: e.target.value,
              // Beim Wechsel auf Uplink das LAN nicht mitschleppen.
              network: e.target.value === "sta" && form.network === "lan" ? "" : form.network,
            })
          }
        >
          <option value="ap">Access Point — versorgt Clients</option>
          <option value="sta">Uplink (Client) — hängt den Router an ein fremdes WLAN</option>
        </select>
      </Field>
      {sta && (
        <Alert variant="info">
          Ein Uplink ist die Leitung des Routers, kein WLAN für Gäste. Er erscheint unter „Internet“
          neben Mobilfunk und Ethernet. Fällt er aus, während er trägt, ist die Box nicht mehr
          erreichbar.
        </Alert>
      )}
      <Input
        label="SSID"
        helperText={sta ? "Die SSID des fremden WLANs, in das sich der Router einbucht." : ""}
        value={form.ssid}
        onChange={(e) => onChange({ ...form, ssid: e.target.value })}
      />
      <Field label="Verschlüsselung" htmlFor="wifi-enc">
        <select
          id="wifi-enc"
          className="ds-input"
          value={form.encryption}
          onChange={(e) => onChange({ ...form, encryption: e.target.value })}
        >
          {ENCRYPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      {!open && (
        <PasswordInput
          label="Schlüssel"
          helperText={
            form.id
              ? "Leer lassen behält den bisherigen Schlüssel — der Router gibt ihn nie heraus."
              : "8 bis 63 Zeichen."
          }
          error={keyTooShort ? "Mindestens 8 Zeichen." : undefined}
          value={form.key}
          onChange={(e) => onChange({ ...form, key: e.target.value })}
        />
      )}
      {open && !sta && (
        <Alert variant="warning">
          Ein offenes WLAN gibt jedem in Funkreichweite Zugang zum LAN des Routers. Nur bewusst und
          befristet einsetzen.
        </Alert>
      )}
      {sta && (
        <Input
          label="BSSID (optional)"
          helperText="Bindet den Uplink an genau einen AP. Leer: der stärkste mit passender SSID."
          placeholder="8e:53:66:d0:cf:d8"
          value={form.bssid}
          onChange={(e) => onChange({ ...form, bssid: e.target.value })}
        />
      )}
      <Input
        label="Netz (uci-Interface)"
        helperText={
          sta ? "Eigenes Interface für den Uplink, z.B. `wwan` — nicht `lan`." : "Meist `lan`."
        }
        error={staInLan ? "Ein Uplink darf nicht im LAN hängen." : undefined}
        value={form.network}
        onChange={(e) => onChange({ ...form, network: e.target.value })}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {!sta && (
          <>
            <Switch
              label="SSID verstecken"
              checked={form.hidden}
              onChange={(v) => onChange({ ...form, hidden: v })}
            />
            <Switch
              label="Clients isolieren"
              checked={form.isolate}
              onChange={(v) => onChange({ ...form, isolate: v })}
            />
          </>
        )}
        <Switch
          label="Abgeschaltet"
          checked={form.disabled}
          onChange={(v) => onChange({ ...form, disabled: v })}
        />
      </div>
    </div>
  );
}

function NetworkModal({
  form,
  busy,
  onChange,
  onClose,
  onSubmit,
}: {
  form: NetForm | null;
  busy: boolean;
  onChange: (form: NetForm) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  if (form === null) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? "WLAN bearbeiten" : "WLAN anlegen"}
      subtitle={`Auf Radio ${form.radio}. Der Router lädt danach nur seine WLAN-Interfaces neu.`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="check"
            loading={busy}
            disabled={netFormErrors(form).blocked}
            onClick={onSubmit}
          >
            Speichern
          </Button>
        </>
      }
    >
      <NetworkFields form={form} onChange={onChange} />
    </Modal>
  );
}

/** Spalten der SSID-Tabelle eines Radios. */
function networkColumns(
  locked: boolean,
  onEdit: (net: WifiNetwork) => void,
  onDelete: (net: WifiNetwork) => void,
): TableColumn<WifiNetwork>[] {
  return [
    {
      key: "mode",
      header: "Rolle",
      cell: (n) =>
        isStation(n) ? (
          <Badge variant="info">Uplink</Badge>
        ) : (
          <Badge variant="neutral">{n.mode === "ap" || !n.mode ? "AP" : n.mode}</Badge>
        ),
    },
    { key: "ssid", header: "SSID", cell: (n) => n.ssid || "—" },
    {
      key: "encryption",
      header: "Verschlüsselung",
      cell: (n) => (
        <Badge variant={!n.encryption || n.encryption === "none" ? "danger" : "success"}>
          {ENCRYPTIONS.find(([v]) => v === n.encryption)?.[1] ?? n.encryption ?? "offen"}
        </Badge>
      ),
    },
    { key: "network", header: "Netz", hideOnMobile: true, cell: (n) => n.network ?? "—" },
    {
      key: "flags",
      header: "",
      hideOnMobile: true,
      cell: (n) => (
        <span className="flex flex-wrap gap-1">
          {n.hidden && <Badge variant="neutral">versteckt</Badge>}
          {n.isolate && <Badge variant="neutral">isoliert</Badge>}
          {n.disabled && <Badge variant="warn">aus</Badge>}
          {/* Zentral gepflegt (MAC-Blockliste) — hier nur sichtbar gemacht. */}
          {n.macfilter === "deny" && (
            <Badge variant="neutral" title={(n.maclist ?? []).join("\n")}>
              Blockliste ({n.maclist?.length ?? 0})
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: "clients",
      header: "Clients",
      num: true,
      cell: (n) => (n.clients !== undefined ? n.clients : "—"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "actions",
      cell: (n) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            iconName="edit"
            disabled={locked}
            onClick={() => onEdit(n)}
          >
            Bearbeiten
          </Button>
          <Button
            size="sm"
            variant="ghost"
            iconName="trash"
            disabled={locked}
            onClick={() => onDelete(n)}
          >
            Löschen
          </Button>
        </div>
      ),
    },
  ];
}

/**
 * Zustand eines Radios. Zwei Quellen, und die uci gewinnt: `up` kommt aus
 * netifd, und direkt nach dem Speichern hat dessen Reload einen abgeschalteten
 * Radio noch nicht abgebaut — die Karte stand dann auf „aktiv“, während der
 * Schalter daneben „abgeschaltet“ zeigte. Nachgeladen wird hier nichts
 * (Datensparsamkeit), also sagt der Badge, was konfiguriert ist.
 */
function RadioBadge({ radio }: { radio: WifiRadio }) {
  if (radio.disabled) return <Badge variant="warn">abgeschaltet</Badge>;
  return (
    <Badge variant={radio.up ? "success" : "warn"} dot>
      {radio.up ? "aktiv" : "läuft nicht"}
    </Badge>
  );
}

/** Hardware-Einstellungen eines Radios. Sie kommen mit der Box — angelegt wird
 *  hier nichts, nur geändert. */
function RadioFields({
  radio,
  locked,
  form,
  onChange,
}: {
  radio: WifiRadio;
  locked: boolean;
  form: RadioForm;
  onChange: (form: RadioForm) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
      <Input
        label="Kanal"
        helperText="Nummer oder auto"
        value={form.channel}
        disabled={locked}
        onChange={(e) => onChange({ ...form, channel: e.target.value })}
      />
      <Input
        label="HT-Modus"
        helperText="z.B. HT20"
        value={form.htmode}
        disabled={locked}
        onChange={(e) => onChange({ ...form, htmode: e.target.value })}
      />
      <Input
        label="Land"
        helperText="DE"
        value={form.country}
        disabled={locked}
        onChange={(e) => onChange({ ...form, country: e.target.value })}
      />
      <Input
        label="Sendeleistung (dBm)"
        inputMode="numeric"
        value={form.txpower}
        disabled={locked}
        onChange={(e) => onChange({ ...form, txpower: e.target.value })}
      />
      <Switch
        label="Radio abschalten"
        checked={form.disabled}
        disabled={locked}
        onChange={(v) => onChange({ ...form, disabled: v })}
        ariaLabel={`Radio ${radio.id} abschalten`}
      />
    </div>
  );
}

interface RadioForm {
  channel: string;
  htmode: string;
  country: string;
  txpower: string;
  disabled: boolean;
}

function toRadioForm(radio: WifiRadio): RadioForm {
  return {
    channel: radio.channel ?? "auto",
    htmode: radio.htmode ?? "",
    country: radio.country ?? "",
    txpower: radio.txpower !== undefined ? String(radio.txpower) : "",
    disabled: radio.disabled ?? false,
  };
}

/** Nur die geänderten Radio-Felder — der Agent lässt den Rest stehen. */
function radioParams(radio: WifiRadio, form: RadioForm): Record<string, unknown> {
  const params: Record<string, unknown> = { id: radio.id };
  const base = toRadioForm(radio);
  if (form.channel.trim() && form.channel !== base.channel) params.channel = form.channel.trim();
  if (form.htmode.trim() && form.htmode !== base.htmode) params.htmode = form.htmode.trim();
  if (form.country.trim() && form.country !== base.country)
    params.country = form.country.trim().toUpperCase();
  if (form.txpower.trim() && form.txpower !== base.txpower) params.txpower = Number(form.txpower);
  if (form.disabled !== base.disabled) params.disabled = form.disabled;
  return params;
}

/**
 * Wie viele Interfaces ein Radio gleichzeitig fahren kann.
 *
 * Der Router meldet das NICHT: die Zahl steckt in den Interface-Kombinationen
 * des Treibers (`iw phy <phy> info`), und dafür gibt es kein ubus-Objekt —
 * `exec` hat der Agent bewusst nicht. 4 ist, was mt76 auf dem MT7620 der Flotte
 * trägt; ein Uplink (`sta`) zählt mit. Deshalb eine Bremse mit Hinweis und
 * keine Sperre, die sich als gemessen ausgibt: wer es besser weiß, tippt
 * weiter — der Router lehnt dann ab, statt dass die Oberfläche lügt.
 */
const MAX_NETWORKS_PER_RADIO = 4;

/** Ein Radio: Hardware-Einstellungen + die SSIDs darauf. */
function RadioCard({
  radio,
  locked,
  busy,
  onSaveRadio,
  onEdit,
  onAdd,
  onDelete,
}: {
  radio: WifiRadio;
  locked: boolean;
  busy: boolean;
  onSaveRadio: (params: Record<string, unknown>) => void;
  onEdit: (net: WifiNetwork) => void;
  onAdd: () => void;
  onDelete: (net: WifiNetwork) => void;
}) {
  const [form, setForm] = useState<RadioForm>(() => toRadioForm(radio));
  const params = radioParams(radio, form);
  const dirty = Object.keys(params).length > 1;
  const full = (radio.networks ?? []).length >= MAX_NETWORKS_PER_RADIO;

  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {radio.id}
          {radio.band && <Badge variant="neutral">{radio.band}</Badge>}
          <RadioBadge radio={radio} />
        </span>
      }
      subtitle={
        radio.channelActive !== undefined && radio.channel === "auto"
          ? `Kanal ${radio.channelActive} (automatisch gewählt)`
          : `Kanal ${radio.channel ?? "—"}`
      }
      actions={
        <Button
          size="sm"
          variant="outline"
          iconName="check"
          loading={busy}
          disabled={!dirty || locked}
          onClick={() => onSaveRadio(params)}
        >
          Radio speichern
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioFields radio={radio} locked={locked} form={form} onChange={setForm} />

        <div className="-mx-4 -mb-4">
          <Table
            columns={networkColumns(locked, onEdit, onDelete)}
            rows={radio.networks ?? []}
            rowKey={(n) => n.id}
            emptyMessage="Auf diesem Radio ist noch keine SSID eingerichtet."
          />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {full && (
            <span className="text-xs text-content-3">
              Max. {MAX_NETWORKS_PER_RADIO} Interfaces je Radio (STA zählt mit)
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            iconName="plus"
            disabled={locked || full}
            title={full ? `Max. ${MAX_NETWORKS_PER_RADIO} Interfaces je Radio` : undefined}
            onClick={onAdd}
          >
            SSID hinzufügen
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Meldung und Probe-Banner. Der Banner erscheint nur, wenn der Server auf
 *  Probe geschrieben hat — also wenn die Änderung einen Uplink betraf. */
function WifiNotices({ probe }: { probe: ReturnType<typeof useConfirmProbe<WifiStatus>> }) {
  return (
    <>
      {probe.note && (
        <Alert variant={probe.note.variant} dismissible onDismiss={() => probe.setNote(null)}>
          {probe.note.text}
        </Alert>
      )}
      <ProbeBanner
        phase={probe.phase}
        secondsLeft={probe.secondsLeft}
        keeping={probe.keeping}
        hint="Diese Änderung betrifft einen WLAN-Uplink. Prüfen, ob der Router noch antwortet — ohne Bestätigung holt er den alten Stand von selbst zurück."
        onKeep={() => void probe.keep()}
        onDismiss={probe.dismiss}
      />
    </>
  );
}

export function WifiSection({ device }: { device: DeviceDetail }) {
  const section = useSection<WifiStatus>(device, "wifi");
  // Zwei-Schritt nur, wo er nötig ist: `wifi_set` schreibt auf Probe, sobald die
  // Änderung einen WLAN-UPLINK betrifft, und sonst direkt. Die Entscheidung
  // fällt auf dem Server (writes.py::_touches_uplink) — nur er weiß, welches
  // Interface ein `sta` ist. Kommt kein Token zurück, war es ein Access Point
  // und der Hook bleibt im Leerlauf.
  const probe = useConfirmProbe(device.id, section, {
    setAction: "wifi_set",
    confirmAction: "wifi_confirm",
    revertIn: WIFI_REVERT_IN,
  });
  const [form, setForm] = useState<NetForm | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WifiNetwork | null>(null);
  const [confirmRadioOff, setConfirmRadioOff] = useState<{
    radio: WifiRadio;
    params: Record<string, unknown>;
  } | null>(null);

  const radios = section.data?.radios ?? [];
  const locked = !device.online || probe.applying || probe.keeping;

  /** Alle Netze aller Radios als Wire-Liste — der Agent löscht, was fehlt. */
  function allNetworks(): Record<string, unknown>[] {
    return radios.flatMap((r) => (r.networks ?? []).map((n) => netParams(toNetForm(n, r.id))));
  }

  async function send(params: Record<string, unknown>) {
    // Auch während einer Probe neu laden: der Router liest die uci samt
    // Probe-Delta, die Anzeige zeigt also, was gerade wirklich läuft.
    if (await probe.apply(params)) await section.refresh();
  }

  async function saveNetwork() {
    const edited = form;
    setForm(null);
    if (!edited) return;
    // Desired-State: die unveränderten Netze müssen mit, sonst sind sie weg.
    const others = allNetworks().filter((n) => n.id !== edited.id);
    await send({ networks: [...others, netParams(edited)] });
  }

  async function deleteNetwork() {
    const target = confirmDelete;
    setConfirmDelete(null);
    if (!target) return;
    await send({ networks: allNetworks().filter((n) => n.id !== target.id) });
  }

  /**
   * Ein Radio abzuschalten, auf dem ein `sta` läuft, kappt die Leitung des
   * Routers selbst — auf einer Box ohne eingebuchtes Modem hängt der
   * Mgmt-Tunnel daran. Deshalb hier nachfragen, bevor es rausgeht; jede andere
   * Radio-Änderung geht direkt durch.
   */
  function saveRadio(radio: WifiRadio, params: Record<string, unknown>) {
    if (params.disabled === true && (radio.networks ?? []).some(isStation)) {
      setConfirmRadioOff({ radio, params });
      return;
    }
    void send({ radios: [params] });
  }

  async function disableRadio() {
    const target = confirmRadioOff;
    setConfirmRadioOff(null);
    if (!target) return;
    await send({ radios: [target.params] });
  }

  return (
    <>
      <SectionCard
        title="WLAN"
        description="Radios und die SSIDs darauf"
        section={section}
        deviceOnline={device.online}
        iconName="wifi"
        emptyDescription="Noch nie abgefragt. „Vom Router laden“ holt Radios und SSIDs vom Router."
        above={<WifiNotices probe={probe} />}
      >
        {(data) => (
          <div className="flex flex-col gap-4">
            {(data.radios ?? []).length === 0 ? (
              <EmptyState
                iconName="wifi"
                title="Kein Radio gefunden"
                description="Dieser Router meldet keine WLAN-Hardware."
              />
            ) : (
              (data.radios ?? []).map((radio) => (
                <RadioCard
                  // Der Key hängt am Stand: nach einem Refresh sollen die
                  // Eingabefelder den frischen Wert zeigen, nicht den getippten.
                  key={`${radio.id}-${section.fetchedAt ?? ""}`}
                  radio={radio}
                  locked={locked}
                  busy={probe.applying}
                  onSaveRadio={(params) => saveRadio(radio, params)}
                  onEdit={(net) => setForm(toNetForm(net, radio.id))}
                  onAdd={() => setForm(emptyNetForm(radio.id))}
                  onDelete={setConfirmDelete}
                />
              ))
            )}
          </div>
        )}
      </SectionCard>

      <NetworkModal
        form={form}
        busy={probe.applying}
        onChange={setForm}
        onClose={() => setForm(null)}
        onSubmit={() => void saveNetwork()}
      />

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="WLAN löschen?"
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="trash"
              loading={probe.applying}
              onClick={() => void deleteNetwork()}
            >
              Löschen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {confirmDelete && isStation(confirmDelete) && (
            <Alert variant="danger" title="Das ist ein Uplink, kein WLAN für Clients">
              Der Router hängt über dieses Interface an einem fremden WLAN
              {confirmDelete.network ? ` (Netz „${confirmDelete.network}“)` : ""}. Trägt es gerade
              die Verbindung, ist die Box nach dem Löschen nicht mehr erreichbar — und niemand kann
              es aus der Ferne zurückholen.
            </Alert>
          )}
          <p className="text-sm text-content-2">
            „{confirmDelete?.ssid || confirmDelete?.id}“ verschwindet vom Router. Geräte, die
            darüber hängen, verlieren die Verbindung.
          </p>
        </div>
      </Modal>

      <Modal
        open={confirmRadioOff !== null}
        onClose={() => setConfirmRadioOff(null)}
        title={`Radio ${confirmRadioOff?.radio.id} abschalten?`}
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirmRadioOff(null)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              iconName="power"
              loading={probe.applying}
              onClick={() => void disableRadio()}
            >
              Trotzdem abschalten
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Alert variant="danger" title="Radio trägt einen Uplink">
            STA:{" "}
            {(confirmRadioOff?.radio.networks ?? [])
              .filter(isStation)
              .map((n) => n.ssid || n.id)
              .join(", ")}
          </Alert>
          <p className="text-sm text-content-2">
            Auf Probe — ohne Bestätigung Rollback nach {WIFI_REVERT_IN} s. Läuft der Mgmt-Kanal über
            diesen STA, ist die Box bis dahin weg.
          </p>
        </div>
      </Modal>
    </>
  );
}
